// components/CheckoutBoletoParceladoAsaas.js
// Props:
//   pedido = { _id, total, usuario: { whatsapp }, evento: { boletoParcelado, dataEvento } }
//   voltar = () => void

import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Receipt, AlertCircle, ExternalLink, ArrowLeft, CheckCircle } from "lucide-react"
import axios from "axios"
import "../styles/boleto-parcelado.css"

export default function CheckoutBoletoParceladoAsaas({ pedido, voltar }) {

  // ── 1. Calcula opções disponíveis pelo vencimento EXATO ────────────────────
  // Regra: último boleto deve vencer no mínimo 3 dias antes do evento
  // Parcela 1 → amanhã (+1 dia)
  // Parcela N → hoje + (N-1)*30 dias
  const MARGEM_DIAS = 3

  const calcVencimento = (n) => {
    const d = new Date()
    d.setDate(d.getDate() + (n === 1 ? 1 : (n - 1) * 30))
    return d
  }

  const dataEvento = pedido?.evento?.dataEvento ? new Date(pedido.evento.dataEvento) : null
  const diasAteEvento = dataEvento
    ? Math.ceil((dataEvento - new Date()) / (1000 * 60 * 60 * 24))
    : 999

  // Data limite = evento - margem
  const dataLimite = dataEvento
    ? new Date(dataEvento.getTime() - MARGEM_DIAS * 24 * 60 * 60 * 1000)
    : null

  // Máximo configurado pelo admin no evento
  const parcelasConfiguradas = pedido?.evento?.boletoParcelado?.parcelasDisponiveis || []
  const maxConfigurado = parcelasConfiguradas.length > 0 ? Math.max(...parcelasConfiguradas) : 1

  // Conta quantas parcelas cabem verificando o vencimento exato de cada uma
  let maxPorTempo = 1
  if (dataLimite) {
    for (let n = 1; n <= maxConfigurado; n++) {
      if (calcVencimento(n) <= dataLimite) {
        maxPorTempo = n
      } else {
        break
      }
    }
  } else {
    maxPorTempo = maxConfigurado
  }

  const limiteFinal = Math.min(maxConfigurado, maxPorTempo)

  // Opções: 1x (à vista) até limiteFinal
  const opcoesDisponiveis = Array.from({ length: limiteFinal }, (_, i) => i + 1)

  // ── 2. Busca taxa de boleto do sistema ─────────────────────────────────────
  const [taxaBoleto, setTaxaBoleto] = useState(null)

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/taxas`)
      .then(({ data }) => {
        const t = data.find((t) => t.metodo === "boleto")
        if (t) setTaxaBoleto(t)
      })
      .catch(() => {})
  }, [])

  // Calcula a taxa para uma dada quantidade de parcelas
  const calcTaxa = (numParcelas) => {
    if (!taxaBoleto) return 0
    const fracao = pedido.total / numParcelas
    return taxaBoleto.tipo === "porcentagem"
      ? fracao * (taxaBoleto.valor / 100)
      : taxaBoleto.valor // fixo por boleto
  }

  // Valor de cada parcela = fração do ingresso + taxa do boleto
  const calcValorParcela = (numParcelas) => {
    return parseFloat((pedido.total / numParcelas + calcTaxa(numParcelas)).toFixed(2))
  }

  const calcTotal = (numParcelas) => {
    return parseFloat((calcValorParcela(numParcelas) * numParcelas).toFixed(2))
  }

  // ── 3. Estado do formulário ────────────────────────────────────────────────
  // Inicia com o maior número disponível (menor valor por parcela)
  const [numeroParcelas, setNumeroParcelas] = useState(
    opcoesDisponiveis[opcoesDisponiveis.length - 1] || 1
  )
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", cpfCnpj: "" })
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState("")
  const [resultado, setResultado] = useState(null)

  // Proteção contra duplicata: verifica se já existem boletos ao montar
  const [verificandoExistente, setVerificandoExistente] = useState(true)

  const valorParcela = calcValorParcela(numeroParcelas)
  const totalFinal = calcTotal(numeroParcelas)
  const taxaParcelaAtual = calcTaxa(numeroParcelas)

  // Preview das datas de vencimento
  const vencimentos = Array.from({ length: numeroParcelas }, (_, i) =>
    calcVencimento(i + 1).toLocaleDateString("pt-BR")
  )

  // ── Ao montar: verifica se já existe parcelado ativo para este pedido ────
  // Se sim, exibe os boletos já gerados em vez do formulário, impedindo duplicata.
  useEffect(() => {
    const verificar = async () => {
      try {
        const token = localStorage.getItem("token")
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/pagamento/parcelado/${pedido._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        // Se encontrou um parcelamento não-cancelado, mostra ele como resultado
        if (data?.status && data.status !== "cancelado") {
          setResultado({ ...data, jaExistia: true })
        }
      } catch {
        // 404 = nenhum parcelamento existente, segue para o formulário normalmente
      } finally {
        setVerificandoExistente(false)
      }
    }
    if (pedido?._id) verificar()
    else setVerificandoExistente(false)
  }, [pedido?._id]) // eslint-disable-line

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setErro("")
    try {
      const token = localStorage.getItem("token")
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/pagamento/asaas/boleto-parcelado`,
        {
          pedidoId: pedido._id,
          totalPedido: pedido.total,
          numeroParcelas,
          nomeVaqueiro: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          cpfCnpj: form.cpfCnpj,
          phone: pedido?.usuario?.whatsapp || "",
          descricao: `Boleto parcelado pedido ${pedido._id}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setResultado(data)
    } catch (err) {
      // Se o backend retornou 409 (parcelado já existe), exibe os boletos existentes
      if (err?.response?.status === 409 && err?.response?.data?.jaExistia) {
        setResultado(err.response.data)
        return
      }
      setErro(
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Erro ao gerar boleto."
      )
    } finally {
      setLoading(false)
    }
  }

  // ── Aguarda verificação de parcelado existente ────────────────────────────
  if (verificandoExistente) {
    return (
      <div className="bp-checkout" style={{ textAlign: "center", padding: "48px 24px" }}>
        <div style={{
          width: 36, height: 36, margin: "0 auto 16px",
          border: "3px solid rgba(255,204,0,0.2)",
          borderTopColor: "#ffcc00",
          borderRadius: "50%",
          animation: "bp-spin 0.8s linear infinite",
        }} />
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
          Verificando pagamentos anteriores...
        </p>
      </div>
    )
  }

  // ── Tela de resultado ──────────────────────────────────────────────────────
  if (resultado) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bp-checkout">
        {/* Banner de aviso quando o usuário já tinha boletos gerados */}
        {resultado.jaExistia && (
          <div style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            padding: "12px 16px", marginBottom: 20,
            background: "rgba(59,130,246,0.1)",
            border: "1px solid rgba(59,130,246,0.35)",
            borderRadius: 10,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>ℹ️</span>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: "#93c5fd", fontSize: 13 }}>
                Você já possui boletos gerados para este pedido.
              </p>
              <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                Abaixo estão os boletos gerados anteriormente. Não é possível gerar novos boletos
                enquanto este parcelamento estiver ativo.
              </p>
            </div>
          </div>
        )}

        <div className="bp-sucesso">
          <CheckCircle size={52} className="bp-sucesso-icon" />
          <h2>{resultado.numeroParcelas === 1 ? "Boleto gerado!" : "Boletos gerados!"}</h2>
          <p>
            {resultado.numeroParcelas === 1
              ? "Pague o boleto até a data de vencimento para garantir sua senha."
              : "Pague cada boleto até a data de vencimento. Sua senha será liberada após o pagamento do último boleto."}
          </p>
        </div>

        <div className="bp-parcelas-lista">
          {resultado.parcelas.map((p) => {
            const [y, m, d] = (p.vencimento || "").split("T")[0].split("-")
            return (
              <div key={p.numero} className="bp-parcela-card">
                <div className="bp-parcela-header">
                  <span className="bp-parcela-num">
                    {resultado.numeroParcelas === 1 ? "Boleto" : `Parcela ${p.numero}/${resultado.numeroParcelas}`}
                  </span>
                  <span className="bp-parcela-valor">R$ {p.valor?.toFixed(2)}</span>
                </div>
                <div className="bp-parcela-venc">📅 Vencimento: {d}/{m}/{y}</div>
                {p.linhaDigitavel && <div className="bp-parcela-linha">{p.linhaDigitavel}</div>}
                {p.boletoUrl && (
                  <a href={p.boletoUrl} target="_blank" rel="noopener noreferrer" className="bp-btn-ver-boleto">
                    <ExternalLink size={14} /> Ver boleto
                  </a>
                )}
              </div>
            )
          })}
        </div>

        {/* Aviso regra dos 5 dias */}
        {resultado.numeroParcelas > 1 && resultado.parcelas?.[0] && (() => {
          const venc1 = new Date(resultado.parcelas[0].vencimento)
          const prazo = new Date(venc1.getTime() + 5 * 24 * 60 * 60 * 1000)
          const fmt = (d) => d.toLocaleDateString("pt-BR")
          return (
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              padding: "12px 16px", marginBottom: 16,
              background: "rgba(251,191,36,0.08)",
              border: "1px solid rgba(251,191,36,0.3)",
              borderRadius: 8,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 13, color: "#fbbf24", lineHeight: 1.6 }}>
                Pague o <strong>1º boleto até {fmt(prazo)}</strong> (5 dias após o
                vencimento em {fmt(venc1)}). Se não houver pagamento até essa data,
                a inscrição será <strong>cancelada automaticamente</strong>.
              </p>
            </div>
          )
        })()}

        <p className="bp-obs">A compensação bancária pode levar até 3 dias úteis após o pagamento.</p>

        <button className="bp-btn-voltar" onClick={voltar}>
          <ArrowLeft size={18} /> Voltar
        </button>
      </motion.div>
    )
  }

  // ── Formulário ─────────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bp-checkout">
      <h2 className="bp-titulo">Boleto Parcelado</h2>

      {erro && (
        <div className="bp-erro">
          <AlertCircle size={18} /> {erro}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bp-form">

        {/* ── Seleção de parcelas ─────────────────────────────────────────── */}
        <div className="bp-secao">
          <label className="bp-label">Em quantas vezes deseja pagar?</label>
          <p className="bp-hint">
            Disponível até {limiteFinal}x
            {dataEvento ? ` (evento em ${diasAteEvento} dias · último boleto até ${dataLimite?.toLocaleDateString("pt-BR")})` : ""}.
          </p>

          <div className="bp-opcoes-grid">
            {opcoesDisponiveis.map((n) => {
              const vp = calcValorParcela(n)
              const tot = calcTotal(n)
              const ativo = numeroParcelas === n
              return (
                <button
                  key={n}
                  type="button"
                  className={`bp-opcao-card ${ativo ? "ativo" : ""}`}
                  onClick={() => setNumeroParcelas(n)}
                >
                  <span className="bp-opcao-nx">{n === 1 ? "À vista" : `${n}x`}</span>
                  <span className="bp-opcao-valor">R$ {vp.toFixed(2)}</span>
                  <span className="bp-opcao-total">Total: R$ {tot.toFixed(2)}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Preview de vencimentos ──────────────────────────────────────── */}
        <div className="bp-secao bp-vencimentos">
          <label className="bp-label">
            {numeroParcelas === 1 ? "Data de vencimento" : "Datas de vencimento"}
          </label>
          <div className="bp-venc-lista">
            {vencimentos.map((data, i) => (
              <div key={i} className="bp-venc-item">
                <span className="bp-venc-num">{numeroParcelas === 1 ? "Único" : `${i + 1}ª`}</span>
                <span className="bp-venc-data">{data}</span>
                <span className="bp-venc-valor">R$ {valorParcela.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="bp-resumo-total">
            Total: <strong>R$ {totalFinal.toFixed(2)}</strong>
            {taxaBoleto && taxaParcelaAtual > 0 && (
              <span className="bp-resumo-taxa">
                {" "}(inclui R$ {taxaParcelaAtual.toFixed(2)} de taxa de boleto por parcela)
              </span>
            )}
            {!taxaBoleto && (
              <span className="bp-resumo-taxa"> (carregando taxa...)</span>
            )}
          </div>
        </div>

        {/* ── Dados pessoais ──────────────────────────────────────────────── */}
        <div className="bp-secao">
          <label className="bp-label">Seus dados para o boleto</label>
          <div className="bp-form-grid">
            <div className="bp-form-group">
              <label htmlFor="firstName">Nome</label>
              <input type="text" id="firstName" name="firstName"
                value={form.firstName} onChange={onChange} placeholder="Seu nome" required />
            </div>
            <div className="bp-form-group">
              <label htmlFor="lastName">Sobrenome</label>
              <input type="text" id="lastName" name="lastName"
                value={form.lastName} onChange={onChange} placeholder="Seu sobrenome" required />
            </div>
          </div>
          <div className="bp-form-group">
            <label htmlFor="email">E-mail</label>
            <input type="email" id="email" name="email"
              value={form.email} onChange={onChange} placeholder="Seu e-mail" required />
          </div>
          <div className="bp-form-group">
            <label htmlFor="cpfCnpj">CPF / CNPJ</label>
            <input type="text" id="cpfCnpj" name="cpfCnpj"
              value={form.cpfCnpj} onChange={onChange} placeholder="Somente números" required />
          </div>
        </div>

        {numeroParcelas > 1 && (
          <p className="bp-aviso-senha">
            🔒 A senha só será liberada após o pagamento do <strong>último boleto</strong>.
          </p>
        )}

        {/* ── Aviso sobre a regra dos 5 dias ──────────────────────────────── */}
        <div style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          padding: "12px 16px",
          background: "rgba(251,191,36,0.08)",
          border: "1px solid rgba(251,191,36,0.3)",
          borderRadius: 8,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: 13, color: "#fbbf24", lineHeight: 1.6 }}>
            <strong>Atenção:</strong> O primeiro boleto vence <strong>amanhã</strong>.
            Caso não seja pago em até <strong>5 dias após o vencimento</strong>, sua
            inscrição será <strong>cancelada automaticamente</strong> e as senhas serão
            liberadas para outros compradores.
          </p>
        </div>

        <div className="bp-form-acoes">
          <button type="button" className="bp-btn-voltar" onClick={voltar}>
            <ArrowLeft size={18} /> Voltar
          </button>
          <button type="submit" className="bp-btn-gerar" disabled={loading || !taxaBoleto}>
            {loading ? (
              <><div className="bp-spinner" /> Gerando...</>
            ) : (
              <><Receipt size={18} />
                {numeroParcelas === 1 ? "Gerar boleto" : `Gerar ${numeroParcelas} boletos`}
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  )
}